/**
 * CI settings modal for /admin/orgs — superadmin-only knobs that
 * the customer-facing Settings page (#547) deliberately doesn't
 * expose: concurrent run cap + artifact retention window. See #554
 * PR 2 for the design; #560 for the future Plan-layer that lifts
 * these from per-org-only to per-tier-with-overrides.
 *
 * UX shape:
 *   - Author both fields; either can be left empty → falls through
 *     to env-default (concurrency: no enforcement; retention: env).
 *   - Lowering retention below the org's CURRENT value triggers an
 *     inline impact preview ("this will delete N worktrees older
 *     than M days") AND requires an explicit confirm checkbox
 *     before Save is enabled. Raising retention, or any concurrency
 *     change, doesn't.
 *
 * The /retention-impact API is debounced (~500ms) so a fast typist
 * doesn't pummel the server. Confirm checkbox is only required when
 * the impact count is > 0 — a no-op decrease (no worktrees on disk
 * are old enough yet) saves freely.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui'
import { adminOrgsAPI, type AdminOrgCiSettings, type AdminOrgRow } from '@/lib/api'

export interface CiSettingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  org: AdminOrgRow
  /** Called after a successful save so the parent can refresh / log. */
  onSaved?: () => void
}

type ParsedField =
  | { kind: 'value'; value: number | null }
  | { kind: 'invalid' }

function parseOptionalInt(raw: string): ParsedField {
  const trimmed = raw.trim()
  if (trimmed === '') return { kind: 'value', value: null }
  const n = Number(trimmed)
  if (!Number.isInteger(n) || n < 0) return { kind: 'invalid' }
  return { kind: 'value', value: n }
}

export function CiSettingsModal({ open, onOpenChange, org, onSaved }: CiSettingsModalProps) {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [settings, setSettings] = useState<AdminOrgCiSettings | null>(null)
  const [concurrencyInput, setConcurrencyInput] = useState('')
  const [retentionInput, setRetentionInput] = useState('')
  const [impact, setImpact] = useState<{ days: number; affected: number } | null>(null)
  const [impactLoading, setImpactLoading] = useState(false)
  const [confirmedDestructive, setConfirmedDestructive] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Load current settings every time the modal opens for a new org.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    setSaveError(null)
    setConfirmedDestructive(false)
    setImpact(null)
    adminOrgsAPI
      .getCiSettings(org.id)
      .then((s) => {
        if (cancelled) return
        setSettings(s)
        setConcurrencyInput(s.maxConcurrentRuns == null ? '' : String(s.maxConcurrentRuns))
        setRetentionInput(s.artifactRetentionDays == null ? '' : String(s.artifactRetentionDays))
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setLoadError(e instanceof Error ? e.message : 'Failed to load')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, org.id])

  const parsedConcurrency = useMemo(() => parseOptionalInt(concurrencyInput), [concurrencyInput])
  const parsedRetention = useMemo(() => parseOptionalInt(retentionInput), [retentionInput])

  const currentRetention = settings?.artifactRetentionDays ?? settings?.envDefaults.artifactRetentionDays ?? 0
  // A decrease is destructive iff the new retention is a concrete
  // number AND strictly less than the effective current. Clearing
  // the field (→ null → env default) is not treated as destructive
  // here — env default ≥ 0 and most often equals 0 anyway; the
  // impact API can still be queried on demand if we ever need to
  // gate it too.
  const newRetention =
    parsedRetention.kind === 'value' && typeof parsedRetention.value === 'number'
      ? parsedRetention.value
      : null
  const isDecrease = newRetention != null && newRetention < currentRetention

  // Debounced retention-impact lookup. Only runs on a decrease.
  const debounceRef = useRef<number | null>(null)
  useEffect(() => {
    if (!open || !isDecrease || newRetention == null) {
      setImpact(null)
      setImpactLoading(false)
      return
    }
    setImpactLoading(true)
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current)
    const days = newRetention
    debounceRef.current = window.setTimeout(() => {
      adminOrgsAPI
        .getRetentionImpact(org.id, days)
        .then((r) => {
          setImpact(r)
          setImpactLoading(false)
        })
        .catch(() => {
          setImpact(null)
          setImpactLoading(false)
        })
    }, 500)
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current)
    }
  }, [open, isDecrease, newRetention, org.id])

  // Reset confirm checkbox when the destructive condition flips off
  // (e.g. operator changes their mind and raises the number again).
  useEffect(() => {
    if (!isDecrease || impact == null || impact.affected === 0) {
      setConfirmedDestructive(false)
    }
  }, [isDecrease, impact])

  const hasInvalidInput = parsedConcurrency.kind === 'invalid' || parsedRetention.kind === 'invalid'
  const destructiveBlocked =
    isDecrease && impact != null && impact.affected > 0 && !confirmedDestructive
  // Save also requires that the initial load succeeded — without
  // `settings` we'd be sending the empty form (`{maxConcurrentRuns:
  // null, artifactRetentionDays: null}`), which would silently CLEAR
  // whatever values the org actually has on the server.
  const canSave =
    !loading && !loadError && settings != null && !hasInvalidInput && !destructiveBlocked && !saving

  async function handleSave(): Promise<void> {
    if (!canSave) return
    setSaving(true)
    setSaveError(null)
    try {
      const patch: { maxConcurrentRuns?: number | null; artifactRetentionDays?: number | null } = {}
      if (parsedConcurrency.kind === 'value') patch.maxConcurrentRuns = parsedConcurrency.value
      if (parsedRetention.kind === 'value') patch.artifactRetentionDays = parsedRetention.value
      await adminOrgsAPI.updateCiSettings(org.id, patch)
      onSaved?.()
      onOpenChange(false)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>CI settings — {org.name}</DialogTitle>
          <DialogDescription>
            Per-org operator knobs. Both fields are optional; leave empty to
            inherit the system default.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {loadError && (
            <p className="text-sm text-destructive" role="alert">
              {loadError}
            </p>
          )}
          {!loading && settings && (
            <>
              <div className="space-y-1">
                <Label htmlFor="ci-concurrency">Maximum concurrent runs</Label>
                <Input
                  id="ci-concurrency"
                  type="number"
                  min={0}
                  step={1}
                  value={concurrencyInput}
                  onChange={(e) => setConcurrencyInput(e.target.value)}
                  placeholder="Unset — no enforcement"
                />
                {parsedConcurrency.kind === 'invalid' && (
                  <p className="text-xs text-destructive">Must be a non-negative integer.</p>
                )}
                <p className="text-xs text-muted-foreground">
                  CI triggers above this cap return 429 with Retry-After. Empty = no
                  enforcement.
                </p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="ci-retention">Artifact retention (days)</Label>
                <Input
                  id="ci-retention"
                  type="number"
                  min={0}
                  step={1}
                  value={retentionInput}
                  onChange={(e) => setRetentionInput(e.target.value)}
                  placeholder={`Unset — system default ${settings.envDefaults.artifactRetentionDays}d`}
                />
                {parsedRetention.kind === 'invalid' && (
                  <p className="text-xs text-destructive">Must be a non-negative integer.</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Worktrees older than this are swept daily at 04:00 UTC. Empty =
                  system default ({settings.envDefaults.artifactRetentionDays}d).
                </p>

                {isDecrease && (
                  <div
                    className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs"
                    role="alert"
                  >
                    {impactLoading && <span>Checking impact…</span>}
                    {!impactLoading && impact != null && impact.affected === 0 && (
                      <span>
                        No existing worktrees would be affected by this decrease.
                      </span>
                    )}
                    {!impactLoading && impact != null && impact.affected > 0 && (
                      <>
                        <p className="font-semibold mb-2">
                          {impact.affected.toLocaleString()} existing worktree
                          {impact.affected === 1 ? '' : 's'} will be deleted on
                          the next sweep ({impact.days}d retention).
                        </p>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={confirmedDestructive}
                            onChange={(e) => setConfirmedDestructive(e.target.checked)}
                          />
                          <span>I understand this will permanently delete forensic data.</span>
                        </label>
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {saveError && (
            <p className="text-sm text-destructive" role="alert">
              {saveError}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
